import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from 'react-query';

import { receivePurchaseOrder } from 'api/purchaseOrder';
import {
  TABLE_FRAME_COLOR,
  TABLE_GRID_COLOR,
} from 'components/modules/DataTable';
import { ReceivedQuantities } from 'types/purchaseOrder';

import {
  convertEnteredQuantity,
  formatReceivedQuantity,
  getItemsPerBox,
  getReceivedForSku,
  MarkPurchaseOrderReceivedModalProps,
  toOrderDetailId,
} from './MarkPurchaseOrderReceivedModal.props';

const MarkPurchaseOrderReceivedModalView: FC<MarkPurchaseOrderReceivedModalProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  onPurchaseOrderUpdated,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const orderDetails = purchaseOrder?.orderDetails ?? [];
  const hasReceivedQuantities =
    Object.keys(purchaseOrder?.receivedQuantities ?? {}).length > 0;
  const [enterAsItems, setEnterAsItems] = useState(false);
  const [enteredAmounts, setEnteredAmounts] = useState<Array<number | ''>>([]);
  const [receivingNotes, setReceivingNotes] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setEnterAsItems(false);
    setEnteredAmounts(orderDetails.map(() => ''));
    setReceivingNotes('');
  }, [isOpen, purchaseOrder?._id]);

  const remainingAmounts = useMemo(
    () =>
      orderDetails.map((detail) => {
        const skuId = toOrderDetailId(detail.skuId);
        const alreadyReceived = getReceivedForSku(
          purchaseOrder?.receivedQuantities,
          skuId,
        );
        const remainingBoxes = Math.max(
          0,
          detail.boxQuantity - alreadyReceived.boxQuantity,
        );
        const remainingItems = Math.max(
          0,
          detail.itemQuantity - alreadyReceived.itemQuantity,
        );

        return {
          remainingBoxes,
          remainingItems,
          itemsPerBox: getItemsPerBox(detail),
        };
      }),
    [orderDetails, purchaseOrder?.receivedQuantities],
  );

  const {
    mutate: submitReceivedQuantities,
    isLoading: isSubmitting,
  } = useMutation(
    () => {
      const receivedQuantities: ReceivedQuantities = {};

      orderDetails.forEach((detail, index) => {
        const skuId = toOrderDetailId(detail.skuId);
        const entered = enteredAmounts[index];
        const amount = entered === '' ? 0 : Number(entered);

        if (!skuId || amount <= 0) {
          return;
        }

        const converted = convertEnteredQuantity(
          amount,
          enterAsItems,
          remainingAmounts[index]?.itemsPerBox ?? 1,
        );
        const current = receivedQuantities[skuId] ?? {
          boxQuantity: 0,
          itemQuantity: 0,
        };

        receivedQuantities[skuId] = {
          boxQuantity: current.boxQuantity + converted.boxQuantity,
          itemQuantity: current.itemQuantity + converted.itemQuantity,
        };
      });

      return receivePurchaseOrder(purchaseOrder?._id || '', {
        receivedQuantities,
        receivingNotes: receivingNotes.trim(),
      });
    },
    {
      onSuccess: (data) => {
        if (data?.item) {
          onPurchaseOrderUpdated?.(data.item);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title:
            data?.item?.status === 'received'
              ? 'Purchase order marked as received'
              : 'Partial receipt recorded',
          isClosable: true,
          duration: 4000,
        });
        onClose();
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to record received quantities',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const hasEnteredAmount = enteredAmounts.some(
    (amount) => amount !== '' && Number(amount) > 0,
  );

  return (
    <Modal
      size="5xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="10px" maxW="1100px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader mt={4} textAlign="center">
          Mark as Received
          {purchaseOrder?.poNumer ? ` - ${purchaseOrder.poNumer}` : ''}
        </ModalHeader>
        <ModalBody pb={4}>
          <Flex mb={4} justify="flex-end">
            <Checkbox
              isChecked={enterAsItems}
              onChange={(event) => setEnterAsItems(event.target.checked)}>
              Enter number of items
            </Checkbox>
          </Flex>

          <Box
            overflowX="auto"
            border="1px solid"
            borderColor={TABLE_FRAME_COLOR}
            borderRadius="8px">
            <Table
              size="sm"
              variant="simple"
              sx={{
                // The same frame and grid lines as DataTable's tables.
                // Column headers are centered in every table.
                th: { textAlign: 'center' },
                'th, td': {
                  borderBottomColor: TABLE_GRID_COLOR,
                  borderRight: '1px solid',
                  borderRightColor: TABLE_GRID_COLOR,
                },
                'th:last-of-type, td:last-of-type': { borderRight: 'none' },
                'tbody tr:last-of-type td': { borderBottom: 'none' },
              }}>
              <Thead>
                <Tr>
                  <Th>Sku</Th>
                  <Th>Art</Th>
                  <Th>Ordered Quantity</Th>
                  {hasReceivedQuantities ? <Th>Received Quantity</Th> : null}
                  <Th>Enter Amount Received</Th>
                </Tr>
              </Thead>
              <Tbody>
                {orderDetails.length === 0 ? (
                  <Tr>
                    <Td colSpan={hasReceivedQuantities ? 5 : 4}>
                      <Text color="gray.500">No order details available</Text>
                    </Td>
                  </Tr>
                ) : (
                  orderDetails.map((detail, index) => {
                    const skuId = toOrderDetailId(detail.skuId);
                    const alreadyReceived = getReceivedForSku(
                      purchaseOrder?.receivedQuantities,
                      skuId,
                    );

                    return (
                      <Tr key={`${skuId}-${detail.artId}-${index}`}>
                        <Td>{detail.sku?.name || '-'}</Td>
                        <Td>{detail.art?.number || '-'}</Td>
                        <Td>
                          {formatReceivedQuantity(
                            detail.boxQuantity,
                            detail.itemQuantity,
                          )}
                        </Td>
                        {hasReceivedQuantities ? (
                          <Td>
                            {formatReceivedQuantity(
                              alreadyReceived.boxQuantity,
                              alreadyReceived.itemQuantity,
                            )}
                          </Td>
                        ) : null}
                        <Td>
                          <Input
                            type="number"
                            min={0}
                            w="140px"
                            value={enteredAmounts[index] ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setEnteredAmounts((current) => {
                                const next = [...current];
                                next[index] =
                                  nextValue === '' ? '' : Number(nextValue);
                                return next;
                              });
                            }}
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
                {orderDetails.length > 0 ? (
                  <Tr>
                    <Td colSpan={hasReceivedQuantities ? 4 : 3} />
                    <Td>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEnteredAmounts(
                            remainingAmounts.map((remaining) =>
                              enterAsItems
                                ? remaining.remainingItems
                                : remaining.remainingBoxes,
                            ),
                          );
                        }}>
                        Mark as Fully Received
                      </Button>
                    </Td>
                  </Tr>
                ) : null}
              </Tbody>
            </Table>
          </Box>

          <Box mt={6}>
            <Text fontSize="12px" color="#afafaf" fontWeight="600" mb={2}>
              Receiving Notes (Required)
            </Text>
            <Textarea
              value={receivingNotes}
              onChange={(event) => setReceivingNotes(event.target.value)}
              placeholder="Describe what was received"
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            isDisabled={isSubmitting}
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            isDisabled={
              !hasEnteredAmount || !purchaseOrder?._id || !receivingNotes.trim()
            }
            onClick={() => submitReceivedQuantities()}>
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MarkPurchaseOrderReceivedModalView;
